import type Result from 'true-myth/result';

import type { HttpFetcherError } from '~/shared/http-fetcher/http-fetcher';
import type { ValidatingHttpFetcherWithBaseUrl } from '~/shared/validating-http-fetcher';

import {
    type ChatAttachmentDownloadResponse,
    type ChatAttachmentListResponse,
    type ChatAttachmentOptions,
    type ChatAttachmentPresignResponse,
    type ChatAttachmentUploadTarget,
    chatAttachmentDownloadResponseSchema,
    chatAttachmentListResponseSchema,
    chatAttachmentOptionsSchema,
    chatAttachmentPresignResponseSchema,
} from '../../data-contract/chat-attachments';

export type ChatAttachmentPresignFile = {
    readonly contentLength?: number;
    /** Id of an existing knowledge base document to attach by reference instead of uploading. */
    readonly documentId?: string;
    readonly fileName: string;
    readonly target: ChatAttachmentUploadTarget;
};

export type CreateChatAttachmentUploadUrlsParams = {
    readonly files: readonly ChatAttachmentPresignFile[];
    readonly threadId: string;
};

export type ChatAttachmentsFetchers = {
    readonly createChatAttachmentUploadUrls: (
        params: CreateChatAttachmentUploadUrlsParams,
    ) => Promise<Result<ChatAttachmentPresignResponse, HttpFetcherError>>;
    readonly fetchChatAttachmentDownloadUrl: (
        attachmentId: string,
    ) => Promise<Result<ChatAttachmentDownloadResponse, HttpFetcherError>>;
    readonly fetchChatAttachmentOptions: () => Promise<Result<ChatAttachmentOptions, HttpFetcherError>>;
    readonly fetchChatAttachments: (
        attachmentIds: readonly string[],
    ) => Promise<Result<ChatAttachmentListResponse, HttpFetcherError>>;
};

export type ChatAttachmentsFetchersDependencies = {
    readonly validatingCobrainerApiFetcher: ValidatingHttpFetcherWithBaseUrl;
};

export const createChatAttachmentsFetchers = (
    dependencies: ChatAttachmentsFetchersDependencies,
): ChatAttachmentsFetchers => {
    const { validatingCobrainerApiFetcher } = dependencies;

    const createChatAttachmentUploadUrls: ChatAttachmentsFetchers['createChatAttachmentUploadUrls'] = async ({
        files,
        threadId,
    }) => {
        return validatingCobrainerApiFetcher.post(chatAttachmentPresignResponseSchema, {
            pathname: '/v1/chat-attachments/presigned-urls',
            requestOptions: {
                body: JSON.stringify({ files, threadId }),
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        });
    };

    const fetchChatAttachments: ChatAttachmentsFetchers['fetchChatAttachments'] = async (attachmentIds) => {
        return validatingCobrainerApiFetcher.get(chatAttachmentListResponseSchema, {
            pathname: '/v1/chat-attachments',
            requestOptions: {
                searchParams: { ids: attachmentIds.join(',') },
            },
        });
    };

    const fetchChatAttachmentOptions: ChatAttachmentsFetchers['fetchChatAttachmentOptions'] = async () => {
        return validatingCobrainerApiFetcher.get(chatAttachmentOptionsSchema, {
            pathname: '/v1/chat-attachments/options',
        });
    };

    const fetchChatAttachmentDownloadUrl: ChatAttachmentsFetchers['fetchChatAttachmentDownloadUrl'] = async (
        attachmentId,
    ) => {
        return validatingCobrainerApiFetcher.get(chatAttachmentDownloadResponseSchema, {
            pathname: `/v1/chat-attachments/${attachmentId}/download`,
        });
    };

    return {
        createChatAttachmentUploadUrls,
        fetchChatAttachmentDownloadUrl,
        fetchChatAttachmentOptions,
        fetchChatAttachments,
    };
};
